/**
 * FAFSA Student Aid Index (SAI) and Pell Grant estimate (BUILD-SPEC.md §4.4).
 *
 * The federal need-analysis methodology is a published, fully deterministic
 * formula (Dept. of Education SAI Formula Guide). This implements the
 * **dependent-student** path of the 2026-27 methodology as a pure function of
 * the inputs and the bundled, cited tables — no inference, no prediction.
 *
 * It is an *estimate to verify*: the formula structure here is exact, but the
 * table values (the income protection allowances, the assessment schedule, the
 * Pell figures) are the reviewer's data-only step, exactly like a jurisdiction's
 * bracket table — so the tile points the user to the official SAI Formula Guide
 * and to their FAFSA Submission Summary to confirm the number (§2.1, §2.3).
 *
 * Scope (stated plainly, like SNAP's contiguous-only and Medicaid's
 * expansion-only): the dependent-student formula. The independent-student
 * variant, the simplified/asset-exempt paths, and the per-state aid are out of
 * scope and deferred — this never invents a figure it cannot compute.
 */
import { Money } from "./money";
import type { FafsaData } from "../data/schemas";

/** Employee-share FICA: 6.2% Social Security up to the wage base + 1.45% Medicare. */
function payrollAllowance(income: number, ssWageBase: number): number {
  const earned = Math.max(0, income);
  const socialSecurity = Math.min(earned, Math.max(0, ssWageBase)) * 0.062;
  const medicare = earned * 0.0145;
  return socialSecurity + medicare;
}

/**
 * The largest household this engine will extrapolate to. The published table
 * stops well below it; beyond that the per-additional-person increment is a
 * linear extension, and past a point it stops describing a household at all. A
 * cap is needed rather than merely tidy: without one a crafted `?size=1e308`
 * link extrapolated to Infinity, and the tile then threw a `RangeError` out of
 * `Money.from` and rendered nothing — a blank page from a URL (SPEC-3 §2.1,
 * §2.7). Same shape as the horizon caps in `finance.ts`.
 */
const MAX_HOUSEHOLD_SIZE = 100;

/** Parents' income protection allowance for a family size, extrapolating above
 * the largest tabulated size by the per-additional-person increment. */
function incomeProtectionAllowance(familySize: number, data: FafsaData): number {
  const requested = Number.isFinite(familySize) ? Math.floor(familySize) : 1;
  const size = Math.min(MAX_HOUSEHOLD_SIZE, Math.max(1, requested));
  const table = data.saiIncomeProtectionAllowance;
  const exact = table[String(size)];
  if (exact !== undefined) return exact;
  const sizes = Object.keys(table)
    .map(Number)
    .sort((a, b) => a - b);
  const min = sizes[0];
  const max = sizes[sizes.length - 1];
  if (min !== undefined && size < min) return table[String(min)] ?? 0;
  if (max !== undefined && size > max) {
    return (table[String(max)] ?? 0) + (size - max) * data.ipaPerAdditionalPerson;
  }
  return 0;
}

/**
 * Progressive assessment of parents' adjusted available income. The lowest rate
 * also applies to negative AAI, so the contribution can be negative — the new
 * SAI allows a negative result, floored later at `saiFloor`.
 */
function assessAdjustedAvailableIncome(aai: number, data: FafsaData): number {
  const brackets = data.aaiAssessment;
  const first = brackets[0];
  if (!first) return 0;
  if (aai <= 0) return aai * first.rate;
  let contribution = 0;
  for (let i = 0; i < brackets.length; i++) {
    const bracket = brackets[i]!;
    const lower = bracket.lowerBound;
    if (aai <= lower) break;
    const upper = brackets[i + 1]?.lowerBound ?? Infinity;
    const taxable = Math.min(aai, upper) - lower;
    contribution += taxable * bracket.rate;
  }
  return contribution;
}

export interface SaiInput {
  /** Parents' total income: AGI plus untaxed income. */
  parentIncome: number;
  /** Federal income tax the parents paid (an allowance against income). */
  parentIncomeTax: number;
  /** Number of people in the parents' household (the IPA family size). */
  familySize: number;
  /** The lower-earning parent's earned income (0 for a single earner); drives
   * the employment expense allowance. */
  lowerEarnerIncome: number;
  /** Parents' reportable net worth (cash, investments; not retirement/home). */
  parentAssets: number;
  /** The student's own total income. */
  studentIncome: number;
  /** Federal income tax the student paid. */
  studentIncomeTax: number;
  /** The student's own reportable net worth. */
  studentAssets: number;
  /** Social Security wage base, for the payroll-tax allowance (from the FICA
   * dataset). */
  ssWageBase: number;
}

export interface SaiResult {
  /** The Student Aid Index (whole dollars, floored at the dataset's `saiFloor`). */
  sai: number;
  parentContribution: number;
  studentContribution: number;
  incomeProtectionAllowance: number;
  payrollAllowance: number;
  employmentExpenseAllowance: number;
  /** Parents' income less all allowances (may be negative). */
  availableIncome: number;
  /** Available income plus the assessed asset contribution. */
  adjustedAvailableIncome: number;
  assetContribution: number;
}

/**
 * The ceiling every dollar input is clamped to. A quadrillion dollars is many
 * orders of magnitude past any household the FAFSA describes, and it leaves
 * enough headroom below `Number.MAX_VALUE` that no product or sum downstream can
 * overflow to Infinity. Without it a crafted `?pinc=1e308` link produced a
 * non-finite SAI, which threw out of `Money.from` and blanked the tile — the
 * §2.1 "no non-finite number reaches the screen" invariant failing as a crash
 * rather than as a bad number.
 */
const MAX_DOLLARS = 1e15;

/** Clamp a user-supplied dollar figure to a finite, bounded value. */
function dollars(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(-MAX_DOLLARS, Math.min(MAX_DOLLARS, value));
}

/** Estimate the dependent-student SAI from the 2026-27 methodology and tables. */
export function estimateSai(rawInput: SaiInput, data: FafsaData): SaiResult {
  // Every dollar field is clamped at the boundary, so the whole computation
  // below is guaranteed finite however hostile the deep link that produced it.
  const input: SaiInput = {
    familySize: rawInput.familySize,
    ssWageBase: dollars(rawInput.ssWageBase),
    parentIncome: dollars(rawInput.parentIncome),
    parentIncomeTax: dollars(rawInput.parentIncomeTax),
    lowerEarnerIncome: dollars(rawInput.lowerEarnerIncome),
    parentAssets: dollars(rawInput.parentAssets),
    studentIncome: dollars(rawInput.studentIncome),
    studentIncomeTax: dollars(rawInput.studentIncomeTax),
    studentAssets: dollars(rawInput.studentAssets),
  };
  const ipa = incomeProtectionAllowance(input.familySize, data);
  const payroll = payrollAllowance(input.parentIncome, input.ssWageBase);
  const eea = Math.min(
    Math.max(0, input.lowerEarnerIncome) * data.employmentExpenseAllowance.rate,
    data.employmentExpenseAllowance.cap,
  );
  const totalAllowances = Math.max(0, input.parentIncomeTax) + payroll + ipa + eea;
  const availableIncome = input.parentIncome - totalAllowances;
  const assetContribution = Math.max(0, input.parentAssets) * data.parentAssetRate;
  const aai = availableIncome + assetContribution;
  const parentContribution = assessAdjustedAvailableIncome(aai, data);

  const studentAvailable =
    input.studentIncome -
    Math.max(0, input.studentIncomeTax) -
    data.studentIncomeProtectionAllowance;
  const studentIncomeContribution = Math.max(0, studentAvailable) * data.studentIncomeRate;
  const studentAssetContribution = Math.max(0, input.studentAssets) * data.studentAssetRate;
  const studentContribution = studentIncomeContribution + studentAssetContribution;

  const raw = parentContribution + studentContribution;
  const sai = Math.max(data.saiFloor, Math.round(raw));

  return {
    sai,
    parentContribution: Math.round(parentContribution),
    studentContribution: Math.round(studentContribution),
    incomeProtectionAllowance: Math.round(ipa),
    payrollAllowance: Math.round(payroll),
    employmentExpenseAllowance: Math.round(eea),
    availableIncome: Math.round(availableIncome),
    adjustedAvailableIncome: Math.round(aai),
    assetContribution: Math.round(assetContribution),
  };
}

export interface PellResult {
  /** Estimated Pell Grant award for the year. */
  award: Money;
  /** Whether the SAI is low enough for any Pell award. */
  eligible: boolean;
}

/**
 * Estimate the Pell Grant from the SAI: a student receives the maximum Pell less
 * their SAI (a positive SAI reduces the award dollar-for-dollar), floored at the
 * minimum Pell, and no award once the SAI reaches the maximum Pell.
 *
 * That floor is an **assumption**, and it used to be described as though it were
 * not. ED's 2026-27 letter is explicit that an SAI-calculated award below the
 * minimum makes the student ineligible for an SAI-calculated award — they get
 * the minimum only if they separately qualify for it on income relative to the
 * poverty line, which this engine does not ask. So between an SAI of $6,656 and
 * $7,394 the figure here is the minimum Pell supplied by exactly the guarantee
 * the old note said "can only raise this estimate": in that band it is the whole
 * of it. Modelling the income-based criteria (family size, filing requirement,
 * poverty guideline, state) would settle it and is a feature rather than a fix.
 *
 * The cutoff is deliberately the maximum Pell rather than the statutory bar.
 * OBBBA (Pub. L. 119-21) prohibits any Pell where the SAI is at least twice the
 * maximum — $14,790 for 2026-27 — so this stops well short of it and cannot
 * promise an award the statute forbids.
 */
export function estimatePell(sai: number, data: FafsaData): PellResult {
  if (sai >= data.maxPellGrant) {
    return { award: Money.zero(), eligible: false };
  }
  const scheduled = data.maxPellGrant - Math.max(0, sai);
  const award = Math.max(data.minPellGrant, scheduled);
  return { award: Money.from(Math.round(award)), eligible: true };
}
